import { useRef, useState } from "react";

export interface UsePinInputProps {
  length?: number;
  onComplete?: (pin: string) => void;
}

export interface UsePinInputReturn {
  handlePinChange: (index: number, value: string) => void;
  handlePinKeyDown: (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => void;
  handlePinPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  isPinComplete: boolean;
  pin: string[];
  pinInputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  pinValue: string;
  resetPin: (shouldFocus?: boolean) => void;
}

export const usePinInput = ({
  length = 4,
  onComplete,
}: UsePinInputProps = {}): UsePinInputReturn => {
  const [pin, setPin] = useState<string[]>(Array(length).fill(""));
  const pinInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Handle PIN input change
  const handlePinChange = (index: number, value: string) => {
    // Only allow alphanumeric characters
    if (value && !/^[a-zA-Z0-9]$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    // Auto-focus next input
    if (value && index < length - 1) {
      pinInputRefs.current[index + 1]?.focus();
    }

    // Check if PIN is complete
    const completedPin = newPin.join("");
    if (completedPin.length === length && onComplete) {
      onComplete(completedPin);
    }
  };

  // Handle PIN input keydown
  const handlePinKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      // Focus previous input on backspace if current is empty
      pinInputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste in PIN input
  const handlePinPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, length);

    if (/^[a-zA-Z0-9]+$/.test(pastedData)) {
      const newPin = [...pin];
      for (let i = 0; i < pastedData.length && i < length; i++) {
        newPin[i] = pastedData[i];
      }
      setPin(newPin);

      // Focus the next empty input or the last one
      const nextIndex = Math.min(pastedData.length, length - 1);
      pinInputRefs.current[nextIndex]?.focus();

      // Check if PIN is complete
      const completedPin = newPin.join("");
      if (completedPin.length === length && onComplete) {
        onComplete(completedPin);
      }
    }
  };

  const resetPin = (shouldFocus = true) => {
    setPin(Array(length).fill(""));
    if (shouldFocus) {
      pinInputRefs.current[0]?.focus();
    }
  };

  // Check if PIN is complete
  const isPinComplete = pin.join("").length === length;
  const pinValue = pin.join("");

  return {
    handlePinChange,
    handlePinKeyDown,
    handlePinPaste,
    isPinComplete,
    pin,
    pinInputRefs,
    pinValue,
    resetPin,
  };
};
